import { useCallback, useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { cloneDeep, compact, omit, sortedUniq } from "lodash";

import { CoreTable } from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useSalesPageV3NextContext } from "@features/sales-v3-next/sales-v3-next-provider";
import {
  makeAdKeyDimension,
  makeAdsetNameDimension,
  makeBreakdownDimension,
  makeCampaignNameDimension,
  makeNameDimension,
} from "@features/sales-v4/dimensions";
import {
  SALES_METRICS_COUNT_V4,
  SALES_METRICS_REPORT_V4,
} from "@gql/sales-page-v4";
import { useNorthbeamLazyQuery } from "@hooks/http";
import {
  type GetSalesMetricsCountV4,
  type GetSalesMetricsCountV4Variables,
  type GetSalesMetricsReportV4,
  type GetSalesMetricsReportV4Variables,
} from "@north-beam/nb-client-side";
import {
  makeBreakdownCount,
  makeCpm,
  makeCtr,
  makeDistinctAdKeyCount,
  makeEcpc,
  makeRev,
  makeRevFt,
  makeRoas,
  makeRoasFt,
  makeSpend,
  makeStatuses,
  makeVisits,
} from "@north-beam/nb-client-side/metrics-v2";
import { jsonHash, slugify, type SalesMetricRow } from "@north-beam/nb-common";
import { createColumnHelper } from "@tanstack/react-table";

import { getNextTabId } from "../utils";

import {
  LoadMoreButtonCell,
  LoadMorePlaceholderCell,
  MetricCell,
  MetricLoaderCell,
  NameCell,
  NameLoaderCell,
} from "./body-cells";
import { StatusCell } from "./body-cells/status-cell";
import { PAGE_LIMIT } from "./constants";
import {
  HeaderCell,
  MetricDetailsPanel,
  NumericFilterForm,
  SelectionHeaderCell,
  StatusFilterForm,
} from "./header-cells";
import { type BreakdownItem, type MetricRow, type ReportRow } from "./types";
import {
  createDataRow,
  createLoadingRow,
  createLoadingRows,
  createLoadMoreRow,
} from "./utils";

export const SalesTable = () => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    dateRangeInterval,
    compareDateRangeInterval,
    timeGranularity,
    activeTab,
    breakdowns,
    expandedTableRowIds,
    setActiveTab,
    setFlyoutId,
    setExpandedTableRowIds,
  } = useSalesPageV3NextContext();

  const [dataRepository, setDataRepository] = useState<
    Record<string, ReportRow[]>
  >({ root: [] });

  const hasComparison = Boolean(compareDateRangeInterval);

  const { loadData } = useNorthbeamLazyQuery<
    GetSalesMetricsReportV4,
    GetSalesMetricsReportV4Variables
  >(SALES_METRICS_REPORT_V4);

  const { loadData: loadCount } = useNorthbeamLazyQuery<
    GetSalesMetricsCountV4,
    GetSalesMetricsCountV4Variables
  >(SALES_METRICS_COUNT_V4);

  const {
    idDimensions,
    nameDimensions,
    rowCountMetrics,
    platformDimension,
    campaignDimension,
    adsetDimension,
  } = useMemo(() => {
    const idDimensions = [];
    const nameDimensions = [];
    const rowCountMetrics = [];

    for (const [index, breakdown] of breakdowns.entries()) {
      const breakdownDimension = makeBreakdownDimension(breakdown.key);
      idDimensions.push(breakdownDimension);
      nameDimensions.push(breakdownDimension);

      if (activeTab === "breakdown" && index < breakdowns.length - 1) {
        rowCountMetrics.push(makeBreakdownCount(breakdowns.map((e) => e.key)));
      }

      if (activeTab !== "breakdown") {
        rowCountMetrics.push(makeDistinctAdKeyCount());
      }
    }

    if (activeTab === "breakdown") {
      if (breakdowns.length === 0) {
        const breakdownDimension = makeBreakdownDimension(
          "Platform (Northbeam)",
        );
        idDimensions.push(breakdownDimension);
        nameDimensions.push(breakdownDimension);
      }
    } else {
      idDimensions.push(makeAdKeyDimension());
      nameDimensions.push(makeNameDimension());
    }

    return {
      idDimensions,
      nameDimensions,
      platformDimension: makeBreakdownDimension("Platform (Northbeam)"),
      campaignDimension: makeCampaignNameDimension(),
      adsetDimension: makeAdsetNameDimension(),
      rowCountMetrics,
    };
  }, [activeTab, breakdowns]);

  const metrics = useMemo(() => {
    return [
      makeSpend(),
      makeRev({
        attributionModelLabel: "Clicks only",
        accountingMode: "accrual",
        attributionWindow: "1",
        isDashboardV2: false,
      }),
      makeRevFt({
        attributionModelLabel: "Clicks only",
        accountingMode: "accrual",
        attributionWindow: "1",
        isDashboardV2: false,
      }),
      makeRoas({
        accountingMode: "accrual",
        attributionWindow: "1",
        isDashboardV2: false,
      }),
      makeRoasFt({
        accountingMode: "accrual",
        attributionWindow: "1",
        isDashboardV2: false,
      }),
      makeVisits(),
      makeCpm(),
      makeCtr(),
      makeEcpc(),
    ];
  }, []);

  const sharedVars = useMemo(() => {
    return {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange: dateRangeInterval,
      compareDateRange: compareDateRangeInterval,
      timeGranularity: timeGranularity === "hourly" ? "hourly" : "daily",
      level: activeTab === "breakdown" ? "campaign" : activeTab,
      // TODO:
      metricIds: [makeStatuses().id, ...metrics.map((e) => e.id)],
      sorting: [{ metricId: "spend", order: "desc" }],
    };
  }, [
    attributionModel,
    attributionWindow,
    accountingMode,
    dateRangeInterval,
    compareDateRangeInterval,
    timeGranularity,
    activeTab,
    metrics,
  ]);

  const loadReport = useCallback(
    async ({
      breakdownPath,
      page,
    }: {
      breakdownPath: BreakdownItem[];
      page: number;
    }) => {
      const level = breakdownPath.length;
      const idDimension = idDimensions[level];
      const nameDimension = nameDimensions[level];
      const rowCountMetric = rowCountMetrics[level];

      const dimensions = [idDimension.id, nameDimension.id];

      if (activeTab !== "breakdown" && idDimension.id === "adKey") {
        dimensions.push(platformDimension.id);

        if (activeTab === "adset") {
          dimensions.push(campaignDimension.id);
        }

        if (activeTab === "ad") {
          dimensions.push(campaignDimension.id, adsetDimension.id);
        }
      }

      const dimensionIds = sortedUniq(compact(dimensions));
      const metricIds = sortedUniq(
        compact([...sharedVars.metricIds, rowCountMetric?.id]),
      );

      const [{ data: countData }, { data: rowData }] = await Promise.all([
        loadCount({
          variables: {
            ...sharedVars,
            dimensionIds,
            metricIds,
            breakdownFilters: breakdownPath,
          },
        }),
        loadData({
          variables: {
            ...sharedVars,
            dimensionIds,
            metricIds,
            breakdownFilters: breakdownPath,
            sorting: [
              ...sharedVars.sorting,
              { dimensionId: idDimension.id, order: "asc" },
            ],
            limit: PAGE_LIMIT,
            offset: page * PAGE_LIMIT,
          },
        }),
      ]);

      const actual = (rowData?.me.salesMetricsReportV4.actual ??
        []) as SalesMetricRow[];
      const comparison = (rowData?.me.salesMetricsReportV4.comparison ??
        []) as SalesMetricRow[];

      const rows: MetricRow[] = actual.map((actualRow) => {
        const nextBreakdownPath = [...breakdownPath];
        const idValue = idDimension.getValue(actualRow) ?? "";
        let id = idValue;

        if (idDimension.id !== "adKey") {
          nextBreakdownPath.push({
            breakdown: idDimension.name,
            value: idValue,
          });
          id = jsonHash(nextBreakdownPath);
        }

        const comparisonRow = comparison.find(
          (e) => idDimension.getValue(e) === idDimension.getValue(actualRow),
        );

        return {
          ...omit(actualRow, "metrics"),
          id,
          breakdownPath: nextBreakdownPath,
          actualMetrics: actualRow.metrics,
          comparisonMetrics: hasComparison
            ? comparisonRow?.metrics ?? {}
            : undefined,
        };
      });

      return {
        total: countData?.me.salesMetricsCountV4.total ?? 0,
        rows,
      };
    },
    [
      sharedVars,
      hasComparison,
      activeTab,
      idDimensions,
      nameDimensions,
      platformDimension,
      campaignDimension,
      adsetDimension,
      rowCountMetrics,
    ],
  );

  useEffect(() => {
    let cancelled = false;

    setDataRepository({
      root: createLoadingRows({ level: 0, parent: "root", breakdownPath: [] }),
    });

    loadReport({ breakdownPath: [], page: 0 }).then((data) => {
      if (cancelled) {
        return;
      }

      const rows: ReportRow[] = data.rows.map((row) =>
        createDataRow({ level: 0, parent: "root", row }),
      );

      if (data.total > data.rows.length) {
        rows.push(
          createLoadMoreRow({
            level: 0,
            parent: "root",
            breakdownPath: [],
            page: 1,
          }),
        );
      }

      setDataRepository({ root: rows });
    });

    return () => {
      cancelled = true;
    };
  }, [loadReport]);

  const columnHelper = createColumnHelper<ReportRow>();

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "selection",
        size: 50,
        header: () => <SelectionHeaderCell />,
      }),
      columnHelper.display({
        id: "name",
        size: 450,
        header: ({ column }) => {
          return (
            <HeaderCell
              title="Name"
              isSorted={column.getIsSorted()}
              onSortingChange={(desc) => column.toggleSorting(desc)}
            />
          );
        },
        cell: ({ row }) => {
          const rowType = row.original.type;
          const rowLevel = row.original.level;

          if (rowType === "loading") {
            return <NameLoaderCell />;
          }

          if (rowType === "load-more") {
            return (
              <LoadMoreButtonCell
                level={rowLevel}
                onLoadMoreClicked={() => {
                  row.loadMore(row.original);
                }}
              />
            );
          }

          return (
            <NameCell
              row={row.original}
              isExpanded={row.isRowExpanded()}
              isBreakdownsSelected={breakdowns.length > 0}
              activeTab={activeTab}
              idDimensions={idDimensions}
              nameDimensions={nameDimensions}
              rowCountMetrics={rowCountMetrics}
              platformDimension={platformDimension}
              campaignDimension={campaignDimension}
              adsetDimension={adsetDimension}
              onClickTitle={() => row.clickRowTitle(row.original)}
              onExpandRow={() => row.expandRow(row.original)}
              onOpenFlyout={() => row.openFlyout(row.original)}
            />
          );
        },
      }),
      columnHelper.display({
        id: "status",
        size: 150,
        header: () => {
          return (
            <HeaderCell
              title="Status"
              filtersPanelRenderer={() => <StatusFilterForm />}
            />
          );
        },
        cell: ({ row }) => {
          const rowType = row.original.type;

          if (rowType === "loading") {
            return <MetricLoaderCell />;
          }

          if (rowType === "load-more") {
            return <LoadMorePlaceholderCell />;
          }

          return <StatusCell row={row.original} />;
        },
      }),
      ...metrics.map((metric) => {
        return columnHelper.display({
          id: metric.id,
          size: 150,
          header: ({ column }) => {
            return (
              <HeaderCell
                titleClassName="justify-end"
                isCustomMetric={metric.isCustom}
                title={metric.name}
                detailsPanelRenderer={() => (
                  <MetricDetailsPanel metric={metric} />
                )}
                isSorted={column.getIsSorted()}
                onSortingChange={(desc) => column.toggleSorting(desc)}
                filtersPanelRenderer={() => <NumericFilterForm />}
              />
            );
          },
          cell: ({ row }) => {
            const rowType = row.original.type;

            if (rowType === "loading") {
              return <MetricLoaderCell />;
            }

            if (rowType === "load-more") {
              return <LoadMorePlaceholderCell />;
            }

            return <MetricCell row={row.original.data} metric={metric} />;
          },
        });
      }),
    ];
  }, [
    activeTab,
    breakdowns,
    idDimensions,
    nameDimensions,
    platformDimension,
    campaignDimension,
    adsetDimension,
    rowCountMetrics,
    metrics,
  ]);

  return (
    <div className="h-full overflow-auto">
      <CoreTable
        headerCellClassName={clsx(
          "h-10 border-b border-v2-secondary sticky top-0 z-2 bg-white",
        )}
        bodyCellClassName={clsx(
          "h-10 border-b border-r border-v2-tertiary bg-inherit",
          "[&.is-last-column]:border-r-0 has-[.load-more-row]:border-r-0",
          "[&.is-pinned-last]:shadow-[0_0_5px_#00000026]",
          "[&.is-pinned-last]:[clip-path:inset(0_-10px_0_0)]",
          "hover:has-[.name-column-cell]:bg-cosmic-4",
          "hover:has-[.name-column-cell]:border-b-cosmic-1",
        )}
        bodyRowOddClassName="bg-white"
        bodyRowEvenClassName="bg-[#fafafe]"
        columns={columns}
        rows={dataRepository["root"]}
        getRowId={(row) => row.id}
        getSubRows={(row) => dataRepository[row.id]}
        pinnedColumnIds={["selection", "name"]}
        expandedRowIds={expandedTableRowIds}
        onClickRowTitle={() => {
          const nextTabId = getNextTabId(activeTab);

          if (!nextTabId) {
            return;
          }

          setActiveTab(nextTabId);

          // if (activeTab === "ad") {
          //   return;
          // }
          // let toTabId: TabId;
          // const key = JSON.parse(row.key);
          // const isBreakdownRow = Array.isArray(key);
          // const currentDepth = breakdowns.findIndex(
          //   (b) => b.key === row.rowMetadata.breakdown,
          // );
          // const noBreakdowns = Array.isArray(key)
          //   ? (key as Dimensions[]).every((k) => !Object.keys(k).length) ||
          //     (breakdowns.length > 0 &&
          //       currentDepth !==
          //         Object.keys(row.rowMetadata.dimensions).length - 1)
          //   : false;
          // if (noBreakdowns) {
          //   addToast("Uncategorized is not searchable", {
          //     appearance: "info",
          //   });
          //   return;
          // }
          // if (activeTab === "breakdown") {
          //   toTabId = "campaign";
          // } else if (activeTab === "campaign") {
          //   toTabId = "adset";
          // } else {
          //   toTabId = "ad";
          // }
          // const newProposedState = {
          //   inclusionAtomicities: isBreakdownRow
          //     ? [
          //         {
          //           atomicity: row.key,
          //           count: row.rowMetadata.deepestAtomicityCounts,
          //         },
          //       ]
          //     : [],
          //   inclusionAdKeys: !isBreakdownRow
          //     ? [
          //         {
          //           adKey: row.key,
          //           dimensions: row.rowMetadata.dimensions,
          //         },
          //       ]
          //     : [],
          //   exclusionAtomicities: [],
          //   exclusionAdKeys: [],
          // } as FiltersState;
          // tabStates[activeTab].setTabState((toTabState) => {
          //   return {
          //     ...toTabState,
          //     proposedFiltersState: newProposedState,
          //     selectAll: false,
          //   };
          // });
          // const platformDimension =
          //   row.rowMetadata.dimensions["Platform (Northbeam)"];
          // if (platformDimension) {
          //   setTabTopPlatforms([platformDimension]);
          // }
          // tabStates[toTabId].setTabState((toTabState) => {
          //   const currentTabState = tabStates[activeTab].tabState;
          //   // reconcile the states
          //   const reconciledStates = reconcileAppliedAndProposedTabFilters(
          //     currentTabState.appliedFiltersState ?? null,
          //     newProposedState,
          //     isDefaultSelectAll,
          //   );
          //   const { toAddExclusions, appliedAdKeyFilters } =
          //     enrichInclusionsAndExclusions({
          //       breakdowns,
          //       reconciledStates,
          //       currentTabState,
          //     });
          //   const newTabState = {
          //     ...toTabState,
          //     selectAll: false,
          //     preselectionDone: false,
          //     appliedFiltersState: {
          //       // apply the reconciled state between the current
          //       inclusionAtomicities: reconciledStates.inclusionAtoms,
          //       exclusionAtomicities: [
          //         ...reconciledStates.exclusionAtoms,
          //         ...toAddExclusions,
          //       ],
          //       campaignExclusionAdKeys:
          //         currentTabState?.appliedFiltersState
          //           ?.campaignExclusionAdKeys,
          //       campaignInclusionAdKeys:
          //         appliedAdKeyFilters?.campaignInclusionFilters,
          //       adsetInclusionAdKeys:
          //         appliedAdKeyFilters?.adsetInclusionFilters,
          //       adsetExclusionAdKeys:
          //         currentTabState?.appliedFiltersState?.adsetExclusionAdKeys,
          //       ...(!isBreakdownRow && {
          //         [`${activeTab}InclusionAdKeys`]: [
          //           {
          //             adKey: row.key,
          //             dimensions: row.rowMetadata.dimensions,
          //           },
          //         ],
          //       }),
          //     },
          //   } as TabState;
          //   prettyLogEventPage({
          //     domain: "Sales Page V3",
          //     action: "onClick",
          //     additionalActionInfo: "cell-tab-state-change",
          //   });
          //   return newTabState;
          // });
          // onActivate(toTabId);
          // if (isBreakdownRow) {
          //   const breakdownKeys = JSON.parse(row.key) as Dimensions[];
          //   if (!breakdownKeys.length) {
          //     return;
          //   }
          //   const expandedIds = breakdownKeys.map((_, index) => {
          //     return JSON.stringify(breakdownKeys.slice(0, index + 1));
          //   });
          //   setExpandedRowIds(expandedIds);
          // } else {
          //   const dimensions = row.rowMetadata.dimensions as Dimensions;
          //   const breakdownPaths = breakdowns.map((_, index) =>
          //     JSON.stringify(
          //       breakdowns.slice(0, index + 1).map((b) => {
          //         return { [b.key]: dimensions[b.key] };
          //       }),
          //     ),
          //   );
          //   setExpandedRowIds([...breakdownPaths]);
          // }
        }}
        onExpandRow={async (row) => {
          const level = row.level + 1;
          const parent = row.id;
          const breakdownPath = row.breakdownPath;

          setExpandedTableRowIds((prev) => {
            if (prev.includes(row.id)) {
              return prev.filter((e) => e !== row.id);
            } else {
              return [...prev, row.id];
            }
          });

          setDataRepository((prev) => {
            return cloneDeep({
              ...prev,
              [parent]: [createLoadingRow({ level, parent, breakdownPath })],
            });
          });

          const data = await loadReport({ breakdownPath, page: 0 });

          const newRows: ReportRow[] = data.rows.map((row) =>
            createDataRow({ level, parent, row }),
          );

          if (data.total > newRows.length) {
            newRows.push(
              createLoadMoreRow({
                level,
                parent,
                breakdownPath,
                page: 1,
              }),
            );
          }

          setDataRepository((prev) => {
            return cloneDeep({
              ...prev,
              [parent]: newRows,
            });
          });
        }}
        onLoadMore={async (row) => {
          if (row.type !== "load-more") {
            return;
          }

          const level = row.level;
          const parent = row.parent;
          const breakdownPath = row.breakdownPath;
          const page = row.page;

          setDataRepository((prev) => {
            const rows = prev[parent].filter((e) => e.type !== "load-more");

            return cloneDeep({
              ...prev,
              [parent]: [
                ...rows,
                createLoadingRow({ level, parent, breakdownPath }),
              ],
            });
          });

          const data = await loadReport({ breakdownPath, page });

          const newRows: ReportRow[] = data.rows.map((row) =>
            createDataRow({ level, parent, row }),
          );

          setDataRepository((prev) => {
            const currentRows = prev[parent].filter(
              (e) => e.type !== "loading",
            );
            const allRows = [...currentRows, ...newRows];

            if (data.total > allRows.length) {
              allRows.push(
                createLoadMoreRow({
                  level,
                  parent,
                  breakdownPath,
                  page: page + 1,
                }),
              );
            }

            return cloneDeep({ ...prev, [parent]: allRows });
          });
        }}
        onOpenFlyout={(row) => {
          const parsedKey = JSON.parse(row.id);

          prettyLogEventPage({
            domain: "Flyout",
            action: "onClick",
            additionalActionInfo: "Open Flyout",
            data: {
              pageContext: { parsedKey },
            },
          });

          setFlyoutId(slugify(parsedKey));
        }}
      />
    </div>
  );
};
